// Pure "why + how to address" copy for the compliance screens. Maps the metrics
// object (from calculatePlanMetrics) to a short { why, fix } explanation per screen,
// or null when the screen has no modeled flag / is not applicable.
//
// Copy discipline: this text explains what the MODELED screen shows and what would
// move it, in plan-design terms. It never claims a formal result, never tells the
// reader what they "should" do, and is safe to render in an exported report (it
// points at design levers, not app controls). It mirrors the honest, directional
// tone the app already uses on the after-tax ACP card.

import { formatCurrency } from './utils.js'

const pct = (value) => `${(Math.max(0, Number(value) || 0)).toFixed(1)}%`
const plural = (n, one, many) => (Number(n) === 1 ? one : many)

export function adpRemediation(m) {
  if (!m?.informalAdpAvailable) return null
  if (m.informalAdpDeemedSatisfied) return null
  if (m.informalAdpStatus !== 'over-basic-threshold') return null
  const why = `The modeled HCE deferral average (${pct(m.informalAdpHCE)}) is above the `
    + `${pct(m.informalAdpLimit)} the NHCE average (${pct(m.informalAdpNHCE)}) permits under the ADP test.`
  const correction = Number(m.informalAdpCorrectionEstimate) || 0
  const fix = correction > 0
    ? `A qualifying safe-harbor design may receive deemed-satisfaction treatment. Without that treatment, this models refunding about `
      + `${formatCurrency(correction)} to ${m.informalAdpCorrectionHceCount} `
      + `${plural(m.informalAdpCorrectionHceCount, 'HCE', 'HCEs')}, or lifting NHCE deferrals to raise the limit.`
    : `A qualifying safe-harbor design may receive deemed-satisfaction treatment; otherwise the modeled flag can be reduced by lowering HCE deferrals `
      + `or raising NHCE deferrals.`
  return { why, fix }
}

export function acpRemediation(m) {
  if (!m?.informalAcpAvailable) return null
  if (m.informalAcpFullyDeemedSatisfied) return null
  // Potential match deemed-satisfaction treatment does not cover after-tax.
  if (m.informalAcpMatchDeemedSatisfied && m.informalAcpAfterTaxPresent) {
    return {
      why: `A qualifying safe-harbor match may receive deemed-satisfaction treatment for the match portion, but employee after-tax `
        + `contributions remain subject to ACP testing, and the modeled spread is over the ${pct(m.informalAcpLimit)} limit.`,
      fix: `Lowering the plan's after-tax cap, or limiting who may use after-tax, pulls the tested spread down.`,
    }
  }
  if (m.informalAcpStatus !== 'over-basic-threshold') return null
  const correction = Number(m.informalAcpCorrectionEstimate) || 0
  const why = `The modeled HCE match + after-tax average (${pct(m.informalAcpHCE)}) is above the `
    + `${pct(m.informalAcpLimit)} the NHCE average (${pct(m.informalAcpNHCE)}) permits under the ACP test.`
  const fix = correction > 0
    ? `This models refunding about ${formatCurrency(correction)} to ${m.informalAcpCorrectionHceCount} `
      + `${plural(m.informalAcpCorrectionHceCount, 'HCE', 'HCEs')}, or raising NHCE match/after-tax to lift the limit.`
    : `HCE match/after-tax would need to come down, or NHCE match/after-tax rise.`
  return { why, fix }
}

export function coverageRemediation(m) {
  const status = m?.coverageOverallStatus
  if (!status || status === 'unavailable' || status === 'passes-ratio' || status === 'passes-average-benefit') {
    return null
  }
  if (status === 'may-pass-average-benefit') {
    return {
      why: `The employer-benefit ratio (${pct(m.informalCoverageRatio)}) is under 70%, so §410(b) would `
        + `depend on the average benefit test rather than the ratio test.`,
      fix: `The average benefit percentages look close, so coverage may hold if the plan's classification is `
        + `reasonable, but that determination is outside what this screen can confirm.`,
    }
  }
  return {
    why: `The employer-benefit ratio (${pct(m.informalCoverageRatio)}) is under 70% and the average benefit `
      + `test does not appear to carry it (NHCE ${pct(m.informalCoverageNHCEBenefitRate)} vs HCE `
      + `${pct(m.informalCoverageHCEBenefitRate)} benefiting).`,
    fix: `Extending employer contributions to more NHCEs (broader eligibility or an allocation that reaches them) `
      + `raises the ratio.`,
  }
}

export function topHeavyMinRemediation(m) {
  if (!m?.isTopHeavy) return null
  const shortfall = Number(m.topHeavyMinShortfall) || 0
  if (shortfall <= 0) return null
  return {
    why: `The plan is modeled as top-heavy, so each non-key participant generally needs an employer contribution `
      + `of at least ${pct(m.topHeavyMinPercent)}. ${m.topHeavyMinShortfallCount} `
      + `${plural(m.topHeavyMinShortfallCount, 'participant is', 'participants are')} short by about `
      + `${formatCurrency(shortfall)} in total.`,
    fix: `A qualifying safe-harbor nonelective of at least ${pct(m.topHeavyMinPercent)} may cover the modeled top-heavy minimum `
      + `for non-key participants; otherwise about ${formatCurrency(shortfall)} of additional employer `
      + `contributions removes this modeled shortfall. Confirm the applicable minimum with the plan administrator.`,
  }
}

export function crossTestingRemediation(m) {
  const ct = m?.crossTesting
  if (!ct?.available) return null
  if (ct.status === 'passes') return null
  if (ct.status === 'gateway-not-met') {
    return {
      why: `${ct.gatewayShortfallCount} ${plural(ct.gatewayShortfallCount, 'NHCE is', 'NHCEs are')} below the `
        + `${pct(ct.gatewayRate)} modeled minimum allocation gateway used before the benefits-basis screen.`,
      fix: `Raising those NHCE allocations to about ${pct(ct.gatewayRate)} of pay `
        + `(around ${formatCurrency(ct.gatewayShortfallTotal)} in total) is what this screen models as meeting the gateway.`,
    }
  }
  return {
    why: `The model flags ${ct.failingRateGroupCount} ${plural(ct.failingRateGroupCount, 'rate group', 'rate groups')} for `
      + `§410(b) review on a benefits basis, so this census may not support the modeled owner-heavy spread.`,
    fix: `Higher NHCE allocations, or a narrower gap between the owner and staff allocation rates, raise the flagged `
      + `rate groups. Older owners relative to staff make cross-testing easier; a younger owner needs a smaller spread.`,
  }
}

const BUILDERS = {
  adp: adpRemediation,
  acp: acpRemediation,
  coverage: coverageRemediation,
  'top-heavy-min': topHeavyMinRemediation,
  'cross-testing': crossTestingRemediation,
}

// Dispatcher used by the UI: returns { why, fix } | null for a screen id.
export function buildScreenRemediation(screenId, metrics) {
  const builder = BUILDERS[screenId]
  return builder ? builder(metrics) : null
}
